import { Card, CardContent } from '@/components/ui/card';
import { SharingStats } from '@/types/sharing';
import {
  Briefcase,
  MessageSquare,
  Users,
  Share2,
  Download,
  Clock,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useDocumentPermissions } from '@/hooks/useDocumentPermissions';

interface SharingStatsProps {
  stats: SharingStats | undefined;
  isLoading: boolean;
  sharedWithMeCount?: number;
}

export function SharingStatsCards({ stats, isLoading, sharedWithMeCount = 0 }: SharingStatsProps) {
  const { shareLimit, planType } = useDocumentPermissions();
  
  // Calcular total de compartilhamentos
  const totalShares = (stats?.casosCompartilhados || 0) + (stats?.chatsCompartilhados || 0);
  
  // Determinar se está no limite
  const isUnlimited = shareLimit === -1;
  const remainingShares = isUnlimited ? Infinity : Math.max(0, shareLimit - totalShares);
  const percentageUsed = isUnlimited ? 0 : shareLimit > 0 ? Math.min(100, (totalShares / shareLimit) * 100) : 0;
  
  // Cor dinâmica baseada no uso
  const getLimitColor = () => {
    if (isUnlimited) return 'text-green-600';
    if (shareLimit === 0) return 'text-gray-500';
    if (percentageUsed >= 100) return 'text-red-600';
    if (percentageUsed >= 80) return 'text-yellow-600';
    return 'text-blue-600';
  };

  const getLimitBgColor = () => {
    if (isUnlimited) return 'bg-green-50 dark:bg-green-950';
    if (shareLimit === 0) return 'bg-gray-50 dark:bg-gray-950';
    if (percentageUsed >= 100) return 'bg-red-50 dark:bg-red-950';
    if (percentageUsed >= 80) return 'bg-yellow-50 dark:bg-yellow-950';
    return 'bg-blue-50 dark:bg-blue-950';
  };

  const statCards = [
    {
      title: 'Casos Compartilhados',
      value: stats?.casosCompartilhados || 0,
      icon: Briefcase,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-950',
    },
    {
      title: 'Chats Compartilhados',
      value: stats?.chatsCompartilhados || 0,
      icon: MessageSquare,
      color: 'text-green-600',
      bgColor: 'bg-green-50 dark:bg-green-950',
    },
    {
      title: 'Recebidos',
      value: sharedWithMeCount,
      icon: Download,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50 dark:bg-orange-950',
    },
    {
      title: 'Membros Ativos',
      value: stats?.membrosAtivos || 0,
      icon: Users,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50 dark:bg-purple-950',
    },
    {
      title: 'Limite do Plano',
      value: isUnlimited ? '∞' : `${totalShares}/${shareLimit}`,
      subtitle: isUnlimited 
        ? 'Ilimitado' 
        : shareLimit === 0
          ? 'Não disponível'
          : remainingShares === 0 
            ? 'Limite atingido' 
            : `Restam ${remainingShares}`,
      icon: Share2,
      color: getLimitColor(),
      bgColor: getLimitBgColor(),
      progressBar: !isUnlimited && shareLimit > 0 ? {
        percentage: percentageUsed,
        color: percentageUsed >= 100 ? 'bg-red-600' : percentageUsed >= 80 ? 'bg-yellow-500' : 'bg-blue-600'
      } : undefined,
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-20 bg-muted rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Definir data-tour IDs para cada card
  const tourIds = [
    'sharing-casos-compartilhados',
    'sharing-chats-compartilhados',
    'sharing-recebidos',
    'sharing-membros-ativos',
    'sharing-limite-plano',
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card 
              key={stat.title} 
              className="hover:shadow-md transition-shadow"
              data-tour={tourIds[index]}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">{stat.title}</p>
                    <p className={`text-3xl font-bold mt-2 ${stat.color || ''}`}>
                      {stat.value}
                    </p>
                    {stat.subtitle && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {stat.subtitle}
                      </p>
                    )}
                    {stat.progressBar && (
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-3">
                        <div 
                          className={`h-2 rounded-full transition-all ${stat.progressBar.color}`}
                          style={{ width: `${Math.min(100, stat.progressBar.percentage)}%` }}
                        />
                      </div>
                    )}
                  </div>
                  <div className={`${stat.bgColor} p-3 rounded-full ml-3`}>
                    <Icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {stats?.ultimaAtividade && (
        <Card data-tour="sharing-ultima-atividade">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>
                Última atividade:{' '}
                {formatDistanceToNow(stats.ultimaAtividade, {
                  addSuffix: true,
                  locale: ptBR,
                })}
              </span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
